/**
 * J7.5 / v1.1.3-b1: Hundo dex section on the Trainer profile.
 *
 * Pulls /api/hundo/dex (oldest first) and groups by source so the trainer
 * can see at a glance where their hundos came from: manual catches, box
 * scans, video frames, CSV imports, etc.
 *
 * Free for every trainer. No paywall.
 *
 * Brand: black + neon green. No emojis, no em dashes.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import { CaughtSprite } from "@/components/CaughtSprite";
import { findPokemon } from "@/lib/pokemon";
import { speciesName } from "@/data/speciesNames";
import { availableRosterForVariant } from "@/lib/variantAvailability";
import {
  DEX_STATE_EVENT,
  listDexEntriesForVariant,
} from "@/lib/dexStateStore";
import { stableMissingSample } from "@/lib/profileMissingSample";
import { prewarmDexSprites } from "@/lib/prewarmSprites";
import { ClipboardList, Sparkles, Trophy } from "lucide-react";
import { InfoBlock, InfoBlockStat } from "@/components/InfoBlock";
import type { FocusDexVariant } from "@/lib/userPriorities";

interface HundoDexEvent {
  id: number;
  pokemonRecordId: number;
  speciesId: number;
  speciesName: string | null;
  source: string;
  isShiny: boolean;
  isLucky: boolean;
  isShadow: boolean;
  isPurified: boolean;
  cp: number | null;
  detectedAt: number;
  notifiedAt: number | null;
}

interface HundoDexResponse {
  events: HundoDexEvent[];
  totalCount: number;
}

const SOURCE_LABEL: Record<string, string> = {
  manual_catch: "Hand entered",
  screenshot_batch: "Dex scan",
  video_frame: "Video scan",
  csv_import: "PokeGenie import",
  review_queue: "Confirmed from review",
  // TODO(b11): coach_deep_scan still referenced in cloudflare/src/lib/hundoDetector.ts type union — keep mapping until worker cleanup in a future build.
  coach_deep_scan: "Coach Deep Scan",
  unknown: "Other",
};

function sourceLabel(s: string): string {
  return SOURCE_LABEL[s] ?? "Other";
}

function speciesLabel(ev: HundoDexEvent): string {
  if (ev.speciesName && ev.speciesName.trim().length > 0) return ev.speciesName;
  const p = findPokemon(ev.speciesId);
  return p?.name ?? `Pokemon #${ev.speciesId}`;
}

function formatDate(ms: number): string {
  try {
    const d = new Date(ms);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export interface HundoDexSectionProps {
  // b13d S3: the bottom "trophy dex" slot is dynamic — Profile passes the
  // trainer's top questionnaire collection focus (e.g. "Lucky Dex") so the
  // heading reflects what they actually chase. Falls back to "Hundo dex".
  title?: string;
  // b15b U3: when the trainer's #1 collection focus maps to a Pokédex variant
  // the local dex-state tracks, Profile passes that variant so this panel
  // renders THAT dex -- its caught sprites + a caught/total count (e.g.
  // "Lucky Dex 12/151") -- instead of the hundo trail. 2026-08-25: widened
  // beyond shiny/lucky to the dexes the new questionnaire tiles link
  // (dynamax / xxs / xxl / shinyLucky). Hundos and legacy multi-tile foci
  // leave this undefined and keep the rich hundo panel.
  priorityVariant?: FocusDexVariant;
  // b18-profile-1: when Profile needs a focus-specific surface that is neither
  // the shiny/lucky variant dex nor the hundo panel, it passes the node here.
  // customSlot takes precedence over priorityVariant and the default hundo
  // panel. This keeps HundoDexSection responsible for its own layout while
  // letting Profile drive which content renders per collection focus.
  customSlot?: ReactNode;
}

export function HundoDexSection({ title, priorityVariant, customSlot }: HundoDexSectionProps = {}) {
  const { isSignedIn, authedFetch } = useAuth();
  const { openNexQuestionnaire } = useSession();
  const heading = title ?? "Hundo dex";

  // b18-profile-1: Profile-supplied focus-specific slot (PvpEventStrip,
  // ShundoComingSoonCard, etc.) takes priority over the variant/hundo fork.
  if (customSlot != null) {
    return <>{customSlot}</>;
  }

  if (priorityVariant) {
    return (
      <PriorityVariantDex
        variant={priorityVariant}
        heading={heading}
        isSignedIn={isSignedIn}
      />
    );
  }
  return (
    <HundoDexPanel
      heading={heading}
      isSignedIn={isSignedIn}
      authedFetch={authedFetch}
      openNexQuestionnaire={openNexQuestionnaire}
    />
  );
}

// b15b U3: local-store-driven variant dex (shiny / lucky). Reads the SAME
// dexStateStore the Pokédex menu uses, so the count here matches the menu and
// the Profile KPI chips. Tapping a sprite opens that species' Dex entry.
//
// 2026-06-27 regression/improvement: the list is capped to 8 rows (~40 sprites
// at 5/row) in BOTH modes; the count chip toggles between CAUGHT and MISSING;
// and the MISSING view shows a session-stable random subset (see
// profileMissingSample) so it does not reshuffle on navigation or toggle.

// 8 rows × 5 sprites per row.
const PRIORITY_DEX_MAX_SPRITES = 40;

/** Human-readable variant name for the empty-state copy. */
const VARIANT_NOUN: Record<FocusDexVariant, string> = {
  shiny: "shiny",
  lucky: "lucky",
  dynamax: "Dynamax",
  xxs: "XXS",
  xxl: "XXL",
  shinyLucky: "shiny lucky",
};

function PriorityVariantDex({
  variant, heading, isSignedIn,
}: {
  variant: FocusDexVariant;
  heading: string;
  isSignedIn: boolean;
}) {
  const [, setLocation] = useLocation();
  const [tick, setTick] = useState(0);
  // Default to MISSING first: the priority dex is a hunting aid, so the most
  // useful default view is what the trainer still needs to catch, not what
  // they already have. The segmented control below is ordered Missing | Caught
  // to match.
  const [mode, setMode] = useState<"caught" | "missing">("missing");

  // Re-read on every dex-state mutation so the panel stays live (U1 parity).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(DEX_STATE_EVENT, bump);
    return () => window.removeEventListener(DEX_STATE_EVENT, bump);
  }, []);

  // Availability roster for this variant: the species that can legitimately
  // exist in Pokémon GO for it. "Missing" is computed against THIS roster, not
  // the full 1..1025 National Dex, so unreleased-for-this-variant species never
  // surface as missing tiles or inflate the count (1.8.7 Task 4).
  const roster = useMemo(() => availableRosterForVariant(variant), [variant]);
  const total = roster ? roster.size : 0;

  // Present (caught) ids for this variant, sorted, capped to the row limit.
  const caughtIds = useMemo(() => {
    void tick;
    return listDexEntriesForVariant(variant)
      .filter((e) => e.state === "present")
      .map((e) => e.dexNumber)
      .sort((a, b) => a - b);
  }, [variant, tick]);

  const present = Math.min(caughtIds.length, total);
  const missingCount = Math.max(0, total - present);

  // Missing ids = the available roster minus what is present. The displayed
  // subset is a session-stable random sample so it stays put across navigation
  // and caught<->missing toggles (only a fresh app launch reshuffles it).
  const missingIds = useMemo(() => {
    void tick;
    const presentSet = new Set(caughtIds);
    const all: number[] = [];
    if (roster) {
      for (const id of Array.from(roster).sort((a, b) => a - b)) {
        if (!presentSet.has(id)) all.push(id);
      }
    }
    return stableMissingSample(all, PRIORITY_DEX_MAX_SPRITES);
  }, [caughtIds, roster, tick]);

  // Caught view uses the SAME session-stable random sampling as the missing
  // view (was: caughtIds.slice(0, 40), which always pinned the first 40 in dex
  // order and went stale for trainers with a near-complete dex). Sampling keeps
  // the shown subset varied across app launches while staying put within a
  // session and across caught<->missing toggles.
  const caughtCapped = useMemo(
    () => stableMissingSample(caughtIds, PRIORITY_DEX_MAX_SPRITES),
    [caughtIds],
  );

  const visibleIds = mode === "caught" ? caughtCapped : missingIds;

  // Prewarm the colour art for the cells about to render so the grid paints
  // complete instead of streaming sprites in one-by-one on a cold cache. Both
  // the caught and missing samples are warmed the instant their id list
  // resolves; toggling caught<->missing warms the other list on demand. This is
  // fire-and-forget and never blocks render — the CaughtSprite silhouette
  // fallback still covers any straggler on a slow connection. See
  // lib/prewarmSprites.ts for the non-blocking / idempotent guarantees.
  const shinyArt = variant === "shiny" || variant === "shinyLucky";
  useEffect(() => {
    if (visibleIds.length > 0) {
      prewarmDexSprites(visibleIds, shinyArt);
    }
  }, [visibleIds, shinyArt]);

  if (!isSignedIn) return null;

  return (
    <InfoBlock
      id="hundo-dex"
      icon={Sparkles}
      title={heading}
      data-testid="section-priority-dex"
      dataAttrs={{ "data-variant": variant, "data-mode": mode }}
    >
      {/* Full-width segmented control: two pills (Caught | Missing) each taking
          half the row, with a clear selected state and per-mode count. It sits
          directly under the heading rather than inside it, so the heading row is
          the same shape here as on every other block. */}
      <div className="mb-3">
        <div
          className="flex w-full rounded-[var(--radius-lg)] p-1"
          role="group"
          data-testid="priority-dex-toggle"
          aria-label="Switch between caught and missing"
          style={{
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-subtle)",
          }}
        >
          {(["missing", "caught"] as const).map((m) => {
            const active = mode === m;
            const value = m === "caught" ? present : missingCount;
            const label = m === "caught" ? "Caught" : "Missing";
            return (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-testid={`priority-dex-toggle-${m}`}
                aria-pressed={active}
                className="flex-1 rounded-[var(--radius-md)] px-3 py-1.5 text-center transition-colors"
                style={{
                  background: active ? "var(--accent-fill)" : "transparent",
                  color: active ? "var(--text-on-accent)" : "var(--text-secondary)",
                  boxShadow: active
                    ? "0 4px 16px color-mix(in srgb, var(--accent-fill) 30%, transparent)"
                    : "none",
                }}
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em]">
                  {label}
                </div>
                <div
                  className="text-base font-bold tabular-nums"
                  data-testid={`priority-dex-count-${m}`}
                >
                  {value}/{total}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {visibleIds.length === 0 ? (
        <div className="rounded-lg p-4 text-xs bg-[var(--surface-sunken)] border border-[var(--border-subtle)] text-[var(--text-secondary)]">
          {mode === "caught"
            ? `No ${VARIANT_NOUN[variant]} entries yet. Record your ${VARIANT_NOUN[variant]} catches in the Pokédex and they show up here automatically.`
            : `Nothing missing. Every ${VARIANT_NOUN[variant]} entry is caught.`}
        </div>
      ) : (
        <ul className="grid grid-cols-5 gap-2 sm:grid-cols-6" data-testid="priority-dex-grid">
          {visibleIds.map((id) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => setLocation(`/pokemon/${id}`)}
                data-testid={`priority-dex-cell-${id}`}
                className="w-full rounded-lg p-1 hover-elevate flex flex-col items-center"
                aria-label={`Open ${speciesName(id)}`}
              >
                {/* Both caught AND missing now render full-colour art via
                    CaughtSprite. Missing cells previously showed the traced
                    silhouette, which was hard to identify; full-colour art (of
                    the correct shiny/normal variant) is far more picturesque and
                    lets trainers recognise a species at a glance. CaughtSprite
                    resolves official artwork for any id and falls back to the
                    silhouette if every image source fails, so a cell is never
                    blank and there is no layout shift. */}
                <CaughtSprite
                  id={id}
                  size={44}
                  shiny={shinyArt}
                  ariaLabel={speciesName(id)}
                />
                {/* Label uses --text-primary (near-white in dark, near-black in
                    light) instead of the faint --text-tertiary grey so the
                    species name pops and stays legible in both themes. */}
                <span className="mt-0.5 w-full truncate text-center text-[8px] leading-tight text-[var(--text-primary)]">
                  {speciesName(id)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </InfoBlock>
  );
}

function HundoDexPanel({
  heading, isSignedIn, authedFetch, openNexQuestionnaire,
}: {
  heading: string;
  isSignedIn: boolean;
  authedFetch: (path: string, init?: RequestInit) => Promise<Response>;
  openNexQuestionnaire: () => void;
}) {

  const q = useQuery<HundoDexResponse>({
    queryKey: ["/api/hundo/dex"],
    enabled: isSignedIn,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const res = await authedFetch("/api/hundo/dex");
      if (!res.ok) throw new Error(`hundo dex failed: ${res.status}`);
      return res.json();
    },
  });

  const groups = useMemo(() => {
    const events = q.data?.events ?? [];
    const bySource = new Map<string, HundoDexEvent[]>();
    for (const ev of events) {
      const key = ev.source || "unknown";
      const arr = bySource.get(key) ?? [];
      arr.push(ev);
      bySource.set(key, arr);
    }
    return Array.from(bySource.entries()).map(([source, evs]) => ({ source, events: evs }));
  }, [q.data]);

  if (!isSignedIn) return null;

  const total = q.data?.totalCount ?? 0;

  return (
    <InfoBlock
      id="hundo-dex"
      icon={Trophy}
      title={heading}
      subtitle="Every perfect IV Pokemon in your dex. Kept forever and synced across every device you sign in on."
      /* b13g G2: subtle hero frame behind the TOTAL so the trainer's hundo count
         reads as the trophy stat of the section. */
      trailing={<InfoBlockStat label="Total" value={total} />}
      data-testid="section-hundo-dex"
    >

      {q.isLoading ? (
        <div className="rounded-lg p-3 text-xs bg-[var(--surface-sunken)] border border-[var(--border-subtle)] text-[var(--text-secondary)]">
          Loading your hundos.
        </div>
      ) : total === 0 ? (
        // b13d S3: empty Hundo dex deep-links into the trainer questionnaire
        // so a new trainer has a concrete next step instead of a dead end.
        <button
          type="button"
          onClick={openNexQuestionnaire}
          data-testid="hundo-dex-empty-questionnaire"
          className="w-full text-left rounded-lg p-4 flex items-center gap-3 bg-[var(--surface-sunken)] border border-[var(--border-subtle)] text-[var(--text-secondary)]"
        >
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-[var(--accent-subtle)] text-[var(--accent-fill)]">
            <ClipboardList className="w-5 h-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-[var(--text-primary)]">
              No hundos yet. Complete your questionnaire
            </span>
            <span className="block text-[11px] mt-0.5 opacity-80">
              Perfect 15 / 15 / 15 Pokemon you add will collect here.
            </span>
          </span>
        </button>
      ) : (
        <div className="space-y-4">
          {groups.map(({ source, events }) => (
            <div key={source}>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--text-secondary)]">
                  {sourceLabel(source)}
                </span>
                <span className="text-[11px] text-[var(--text-tertiary)]">({events.length})</span>
              </div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {events.map((ev) => (
                  <li
                    key={ev.id}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 bg-[var(--surface-sunken)] border border-[var(--border-subtle)]"
                    data-testid={`hundo-row-${ev.id}`}
                  >
                    <PokemonSilhouette id={ev.speciesId} size={36} caught shiny={ev.isShiny} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-[var(--text-primary)]">
                        {speciesLabel(ev)}
                      </div>
                      <div className="text-[11px] text-[var(--text-secondary)]">
                        IV 15 / 15 / 15
                        {ev.cp ? ` . CP ${ev.cp}` : ""}
                        {ev.isShiny ? " . shiny" : ""}
                        {ev.isLucky ? " . lucky" : ""}
                        {ev.isShadow ? " . shadow" : ""}
                        {ev.isPurified ? " . purified" : ""}
                      </div>
                    </div>
                    <div className="shrink-0 text-[10px] uppercase tracking-[0.16em] text-[var(--text-tertiary)]">
                      {formatDate(ev.detectedAt)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </InfoBlock>
  );
}
